import React, { useState, useEffect } from 'react';
import { X, ArrowRight, Sparkles } from 'lucide-react';

/**
 * Domain Migration Banner
 * Shown when env REACT_APP_SHOW_MIGRATION_BANNER=true
 * Dismissible (persists in localStorage so user sees it once)
 *
 * Activate via /app/frontend/.env:
 *   REACT_APP_SHOW_MIGRATION_BANNER=true
 *   REACT_APP_NEW_DOMAIN=https://practiq.co
 */
const MigrationBanner = () => {
  const showFlag = (process.env.REACT_APP_SHOW_MIGRATION_BANNER || 'false').toLowerCase() === 'true';
  const newDomain = process.env.REACT_APP_NEW_DOMAIN || 'https://practiq.co';
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (!showFlag) return;
    const isDismissed = localStorage.getItem('practiq_migration_banner_dismissed') === 'true';
    setDismissed(isDismissed);
  }, [showFlag]);

  if (!showFlag || dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem('practiq_migration_banner_dismissed', 'true');
    setDismissed(true);
  };

  const handleVisit = () => {
    window.location.href = newDomain;
  };

  // Check if user is already on the new domain
  const currentHost = typeof window !== 'undefined' ? window.location.host : '';
  const newHost = newDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
  if (currentHost === newHost) return null;

  return (
    <div
      className="relative w-full bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 text-white shadow-md"
      data-testid="migration-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <Sparkles className="w-4 h-4 flex-shrink-0 text-yellow-200" />
          <p className="text-sm leading-snug truncate sm:whitespace-normal">
            <span className="font-semibold">We've moved!</span>{' '}
            <span className="hidden sm:inline">
              PractiQ now lives at{' '}
            </span>
            <button
              onClick={handleVisit}
              className="underline font-semibold hover:text-yellow-200 transition-colors"
              data-testid="migration-banner-link"
            >
              {newHost}
            </button>
            <span className="hidden md:inline ml-2 text-white/80">
              — please update your bookmarks
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleVisit}
            className="hidden sm:flex items-center gap-1 px-3 py-1 bg-white/15 hover:bg-white/25 rounded text-xs font-medium transition-colors"
            data-testid="migration-banner-visit"
          >
            Visit New Site
            <ArrowRight className="w-3 h-3" />
          </button>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-white/15 rounded transition-colors"
            aria-label="Dismiss"
            data-testid="migration-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MigrationBanner;
